import React from 'react';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import MetadataBadge, {
  MetadataBadgeVariant,
} from '../../../shared/components/metadata-badge/MetadataBadge';
import BrandIcon from '../../../shared/components/brand-icon/BrandIcon';
import { getPlatformIcon } from '../../../shared/constants/botsActivityIcons';
import { getBotColor } from '../../../shared/constants/botsActivityColors';
import {
  BotAccessAgent,
  BotAccessCategory,
} from '../../../shared/types/bot-access/BotAccessAgent';
import './BotStatusTable.scss';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';

export interface BotStatusTableProps {
  data: BotAccessAgent[];
}

const statusVariantMap: Record<BotAccessAgent['status'], MetadataBadgeVariant> = {
  Allowed: 'success',
  Blocked: 'danger',
  'Not Mentioned': 'pending',
  Unknown: 'secondary',
};

const statusLabelKey: Record<BotAccessAgent['status'], string> = {
  Allowed: 'botAccess.statusOpen',
  Blocked: 'botAccess.statusBlocked',
  'Not Mentioned': 'botAccess.statusDefaultAllow',
  Unknown: 'botAccess.statusNotVerified',
};

const categoryLabelKey: Record<BotAccessCategory, string> = {
  Training: 'botAccess.categoryTraining',
  Search: 'botAccess.categorySearch',
  UserQuery: 'botAccess.categoryAgent',
  Assistant: 'botAccess.categoryAgent',
  Ecosystem: 'botAccess.categoryEcosystem',
};

const BotStatusTable: React.FC<BotStatusTableProps> = ({ data }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const indexBody = (_row: BotAccessAgent, options: { rowIndex: number }) => (
    <span className="bot-status-table__index">{options.rowIndex + 1}</span>
  );

  const botBody = (row: BotAccessAgent) => {
    const platform = row.platform ?? '';
    return (
      <div className="bot-status-table__bot">
        <BrandIcon
          iconClass={getPlatformIcon(platform)}
          color={getBotColor(platform)}
          name={row.agentName ?? platform}
          size="md"
        />
        <span className="bot-status-table__name">{row.agentName ?? '—'}</span>
      </div>
    );
  };

  const typeBody = (row: BotAccessAgent) => {
    if (!row.category) return <span className="bot-status-table__muted">—</span>;
    return <MetadataBadge variant="secondary" text={t(categoryLabelKey[row.category])} />;
  };

  const platformBody = (row: BotAccessAgent) => {
    const platform = row.platform ?? '';
    return (
      <div className="bot-status-table__platform">
        <BrandIcon
          iconClass={getPlatformIcon(platform)}
          color={getBotColor(platform)}
          name={platform}
          size="sm"
        />
        <span>{row.platform ?? '—'}</span>
      </div>
    );
  };

  const statusBody = (row: BotAccessAgent) => (
    <MetadataBadge variant={statusVariantMap[row.status]} text={t(statusLabelKey[row.status])} />
  );

  return (
    <DataTable
      stripedRows
      removableSort
      value={data}
      dataKey="agentId"
      tableClassName="table"
      className="table-wrapper table-wrapper--stack bot-status-table"
      responsiveLayout="stack"
      emptyMessage={t('botAccess.noData')}
    >
      <Column
        header="#"
        headerClassName="table__header bot-status-table__col-index"
        bodyClassName="bot-status-table__col-index"
        body={indexBody}
        style={{ width: '60px' }}
        // Telefon: bez numeru — karty i tak idą po kolei.
        hidden={isMobile}
      />
      <Column
        sortable
        field="agentName"
        header={t('botAccess.colBot')}
        headerClassName="table__header"
        bodyClassName="table__cell--stack-title"
        body={botBody}
        style={{ minWidth: '220px' }}
      />
      <Column
        sortable
        field="category"
        header={t('botAccess.colType')}
        headerClassName="table__header"
        body={typeBody}
        style={{ minWidth: '140px' }}
      />
      <Column
        sortable
        field="platform"
        header={t('botAccess.colPlatform')}
        headerClassName="table__header"
        body={platformBody}
        style={{ minWidth: '180px' }}
      />
      <Column
        sortable
        field="status"
        header={t('botAccess.colStatus')}
        headerClassName="table__header"
        body={statusBody}
        style={{ minWidth: '140px' }}
      />
    </DataTable>
  );
};

export { BotStatusTable };
export default BotStatusTable;
